// Блок рецептуры на карточке стадии короткого пути (stages.config.show_recipe,
// модуль recipes): состав с долями и допусками, режим и расчёт навески по
// массе замеса.  Расчёт только на экране — никуда не сохраняется.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/lib/format";
import type { StageRecipe as Recipe } from "@/lib/types";

const num = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 3 });

export function StageRecipe({ recipe }: { recipe: Recipe | null }) {
  const { t } = useTranslation();
  const [mass, setMass] = useState("");
  if (!recipe) return <p className="text-xs text-text-muted">{t("recipes.none")}</p>;
  const m = Number(mass.replace(",", "."));
  const kg = mass.trim() !== "" && Number.isFinite(m) && m > 0 ? (pct: number) => (Math.max(0, pct) / 100) * m : null;

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs">
          {recipe.code} · v{recipe.version}
        </span>
        <span>{recipe.name}</span>
        <span className="pill pill-warn text-xs">{t("recipes.confidential")}</span>
        {recipe.approved_at && (
          <span className="text-xs text-text-muted">
            {t("recipes.approvedBy")}: {recipe.approver_name ?? "—"} · {formatDateTime(recipe.approved_at)}
          </span>
        )}
      </div>
      <label className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-text-muted">{t("recipes.batchMass")}</span>
        <input
          className="w-28 px-2 py-1 rounded-md bg-surface border border-border font-mono"
          inputMode="decimal"
          value={mass}
          onChange={(e) => setMass(e.target.value)}
        />
        <span className="text-text-muted">{t("recipes.batchHint")}</span>
      </label>
      <table className="w-full text-xs">
        <thead className="text-text-muted">
          <tr>
            <th className="py-1 text-left">{t("recipes.col.component")}</th>
            <th className="py-1 text-right">{t("recipes.col.share")}</th>
            <th className="py-1 text-right">{t("recipes.col.tolerance")}</th>
            {kg && <th className="py-1 text-right">{t("recipes.col.mass")}</th>}
          </tr>
        </thead>
        <tbody>
          {recipe.components.map((c, i) => (
            <tr key={i} className="border-t border-border">
              <td className="py-1">
                {c.component}
                {c.grade && <span className="text-text-muted"> · {c.grade}</span>}
              </td>
              <td className="py-1 text-right font-mono">{num(c.share_pct)}</td>
              <td className="py-1 text-right font-mono">{c.tolerance != null ? `±${num(c.tolerance)}` : "—"}</td>
              {kg && (
                <td className="py-1 text-right font-mono">
                  {num(kg(c.share_pct))}
                  {c.tolerance ? (
                    <span className="text-text-muted">
                      {" "}
                      ({num(kg(c.share_pct - c.tolerance))}…{num(kg(c.share_pct + c.tolerance))})
                    </span>
                  ) : null}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {recipe.params.length > 0 && (
        <dl className="space-y-0.5 text-xs">
          {recipe.params.map((p) => (
            <div key={p.key}>
              <dt className="inline text-text-muted">{p.label}: </dt>
              <dd className="inline font-mono">{p.value ?? "—"}</dd>
              {p.control && <span className="text-text-muted"> ({p.control})</span>}
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
